import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'
import { EventRule, Operator, Rule, RuleGroup, RuleType, VariableSuggestions, WrapperRule } from '../../../types'
import { createUuid } from '../../../utils'

export interface GroupedRule extends Omit<Rule, 'value'> {
    value?: string | string[]
}

export const trimPathDisplay = (path: string = '') => path.startsWith('$.') ? path.substring(2) : path

export const isEventWrapper = (rule: Rule): rule is EventRule => {
    return rule.group === 'event'
        && (rule.path === '$.name' || rule.path === 'name')
}

export const isWrapper = (rule: Rule | GroupedRule): rule is WrapperRule => {
    return rule.type === 'wrapper'
        && ['parent', 'event', 'root'].includes(rule.group)
}

const setRootUuid = (uuid: string, children: Rule[]) => {
    for (const child of children) {
        child.root_uuid = uuid
        if (isWrapper(child) && child.children.length) {
            setRootUuid(uuid, child.children)
        }
    }
}

export const createWrapperRule = (
    group: 'parent' | 'root' = 'parent',
    children: Rule[] = [],
): WrapperRule => {
    const uuid = createUuid()
    setRootUuid(uuid, children)

    return {
        uuid,
        path: '$',
        type: 'wrapper',
        group,
        operator: 'and',
        children: children.map(child => ({
            ...child,
            parent_uuid: uuid,
            root_uuid: uuid,
        })),
    }
}

export const createListRule = (parent: Rule): Rule => ({
    uuid: createUuid(),
    root_uuid: parent.root_uuid ?? parent.uuid,
    parent_uuid: parent.uuid,
    path: '$',
    type: 'list',
    group: 'parent',
    value: '',
    operator: 'any',
})

export const createEventRule = (parent?: Rule, value = ''): EventRule => {
    const base: EventRule = {
        uuid: createUuid(),
        path: '$.name',
        type: 'wrapper',
        group: 'event',
        value,
        operator: 'and',
        children: [],
        frequency: {
            period: {
                type: 'rolling',
                unit: 'day',
                value: 30,
            },
            operator: '>=',
            count: 1,
        },
    }
    if (parent) {
        return {
            ...base,
            root_uuid: parent.root_uuid ?? parent.uuid,
            parent_uuid: parent.uuid,
        }
    }
    return base
}

export const emptySuggestions: VariableSuggestions = {
    userPaths: [],
    eventPaths: {},
}

export const VariablesContext = createContext<{
    suggestions: VariableSuggestions
    listConditionsEnabled?: boolean
}>({
    suggestions: emptySuggestions,
    listConditionsEnabled: false,
})

export interface RuleErrors {
    errors: Record<string, string>
    clearError: (uuid: string) => void
}

export const RuleErrorsContext = createContext<RuleErrors>({
    errors: {},
    clearError: () => {},
})

export interface RuleTemplateErrorDetails {
    rule_uuid: string
    group: RuleGroup
    type: RuleType
    path: string
    operator: Operator
    reason: string
    step_id?: string
}

export const ruleTemplateErrorDetails = (error: any): RuleTemplateErrorDetails | undefined => {
    const data = error?.response?.data
    return data?.code === 7000 && data.details?.rule_uuid ? data.details : undefined
}

export const ruleTemplateErrorMessage = (t: (key: string, options?: Record<string, string>) => string, details: RuleTemplateErrorDetails) => {
    const operator = getOperatorTypes(t)[details.type]?.find(option => option.key === details.operator)?.label ?? details.operator
    return t('rule_template_error', {
        rule: `${details.group}.${trimPathDisplay(details.path)}`,
        operator,
        reason: details.reason,
    })
}

const scrollParent = (element: HTMLElement): HTMLElement => {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) return parent
    }
    return (document.scrollingElement ?? document.documentElement) as HTMLElement
}

export const scrollIntoViewVertically = (element: HTMLElement) => {
    const scroller = scrollParent(element)
    const view = scroller === document.scrollingElement
        ? { top: 0, bottom: window.innerHeight }
        : scroller.getBoundingClientRect()
    const rect = element.getBoundingClientRect()
    if (rect.top >= view.top && rect.bottom <= view.bottom) return
    scroller.scrollBy({ top: rect.top - view.top - (view.bottom - view.top - rect.height) / 2, behavior: 'smooth' })
}

export const useRuleErrors = () => {
    const [errors, setErrors] = useState<Record<string, string>>({})
    const clearError = useCallback((uuid: string) => {
        setErrors(errors => Object.fromEntries(Object.entries(errors).filter(([key]) => key !== uuid)))
    }, [])
    const setFromResponse = useCallback((error: any) => {
        const details = ruleTemplateErrorDetails(error)
        setErrors(details ? { [details.rule_uuid]: details.reason } : {})
        return details
    }, [])
    const value = useMemo(() => ({ errors, clearError }), [errors, clearError])
    return { value, setFromResponse }
}

type TFunction = (key: string) => string

export const getRuleTypes = (t: TFunction): Array<{
    key: RuleType
    label: string
}> => [
    { key: 'string', label: t('rule_type_string') },
    { key: 'number', label: t('rule_type_number') },
    { key: 'boolean', label: t('rule_type_boolean') },
    { key: 'date', label: t('rule_type_date') },
    { key: 'array', label: t('rule_type_array') },
    { key: 'version', label: t('rule_type_version') },
]

interface OperatorOption {
    key: Operator
    label: string
}

export const getOperatorTypes = (t: TFunction): Record<RuleType, OperatorOption[]> => {
    const baseOperators: OperatorOption[] = [
        { key: '=', label: t('op_equals') },
        { key: '!=', label: t('op_does_not_equal') },
        { key: 'is set', label: t('op_is_set') },
        { key: 'is not set', label: t('op_is_not_set') },
    ]
    return {
        string: [
            ...baseOperators,
            { key: 'empty', label: t('op_is_empty') },
            { key: 'contains', label: t('op_contains') },
            { key: 'not contain', label: t('op_does_not_contain') },
            { key: 'starts with', label: t('op_starts_with') },
            { key: 'not start with', label: t('op_does_not_start_with') },
            { key: 'ends with', label: t('op_ends_with') },
        ],
        number: [
            ...baseOperators,
            { key: '<', label: t('op_is_less_than') },
            { key: '<=', label: t('op_is_less_than_or_equal') },
            { key: '>', label: t('op_is_greater_than') },
            { key: '>=', label: t('op_is_greater_than_or_equal') },
        ],
        boolean: [
            { key: '=', label: t('op_is') },
            { key: '!=', label: t('op_is_not') },
        ],
        date: [
            ...baseOperators,
            { key: '<', label: t('op_is_before') },
            { key: '<=', label: t('op_is_on_or_before') },
            { key: '>', label: t('op_is_after') },
            { key: '>=', label: t('op_is_on_or_after') },
            { key: 'is same day', label: t('op_is_same_day') },
        ],
        version: [
            { key: '=', label: t('op_is') },
            { key: '!=', label: t('op_is_not') },
            { key: 'is set', label: t('op_is_set') },
            { key: 'is not set', label: t('op_is_not_set') },
            { key: '>', label: t('op_is_newer_than') },
            { key: '>=', label: t('op_is_at_least') },
            { key: '<', label: t('op_is_older_than') },
            { key: '<=', label: t('op_is_at_most') },
        ],
        array: [
            { key: 'is set', label: t('op_is_set') },
            { key: 'is not set', label: t('op_is_not_set') },
            { key: 'empty', label: t('op_is_empty') },
            { key: 'contains', label: t('op_contains') },
            { key: 'not contain', label: t('op_does_not_contain') },
        ],
        wrapper: [
            { key: 'or', label: t('op_any') },
            { key: 'and', label: t('op_all') },
        ],
        list: [
            { key: 'any', label: t('op_is_in_static_list') },
            { key: 'none', label: t('op_is_not_in_static_list') },
        ],
    }
}

export const getFrequencyOperators = (t: TFunction): OperatorOption[] => [
    { key: '=', label: t('op_exactly') },
    { key: '<', label: t('op_less_than') },
    { key: '<=', label: t('op_less_than_or_equal') },
    { key: '>', label: t('op_greater_than') },
    { key: '>=', label: t('op_greater_than_or_equal') },
]

export type PeriodUnit = 'minute' | 'hour' | 'day' | 'week' | 'month'

export const getPeriodUnits = (t: TFunction): Array<{ key: PeriodUnit, label: string }> => [
    { key: 'minute', label: t('minutes') },
    { key: 'hour', label: t('hours') },
    { key: 'day', label: t('days') },
    { key: 'week', label: t('weeks') },
    { key: 'month', label: t('months') },
]

// Rules cannot select data older than 12 months. Caps per unit match
// that ceiling so switching units after entering a value still stays
// within the one-year window.
export const MAX_PERIOD_VALUES: Record<PeriodUnit, number> = {
    minute: 525600,
    hour: 8760,
    day: 365,
    week: 52,
    month: 12,
}

export const clampPeriodValue = (unit: PeriodUnit, value: number): number => {
    if (!Number.isFinite(value) || value < 1) return 1
    return Math.min(Math.floor(value), MAX_PERIOD_VALUES[unit])
}

// String and version rules accept a list of values for is / is not, so the value
// input becomes a TagInput and the stored value becomes an array
export const isMultiValueRule = (type: RuleType, operator: Operator) => {
    return (type === 'string' || type === 'version') && (operator === '=' || operator === '!=')
}

const textMatchOperators: Operator[] = ['=', '!=', 'contains', 'not contain', 'starts with', 'not start with', 'ends with']

export const isTextMatchRule = (rule: Rule | GroupedRule) => {
    return (rule.type === 'string' || rule.type === 'array') && textMatchOperators.includes(rule.operator)
}

// An absent flag means exact case, so a rule saved before the toggle existed
// keeps its behavior until someone deliberately turns capitalization off
export const matchesExactCase = (rule: Rule | GroupedRule) => rule.case_sensitive !== false

// Text matching ignores capitalization unless asked otherwise, so a marketer
// targeting "premium" also reaches users stored as "Premium"
export const createFilterRule = (root: Rule, parent: Rule, group: RuleGroup): Rule => ({
    uuid: createUuid(),
    root_uuid: root.uuid,
    parent_uuid: parent.uuid,
    path: '',
    type: 'string',
    group,
    value: '',
    operator: '=',
    case_sensitive: false,
})

export interface RuleEditProps<T extends Rule = Rule> {
    rule: T
    root: Rule
    setRule: (value: T) => void
    group: RuleGroup
    eventName?: string
    depth?: number
    controls?: ReactNode
    headerPrefix?: ReactNode
    headerActions?: ReactNode
    index?: number
    maxRuleSets?: number
    allowJourney?: boolean
}
